'use client';

import * as React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';
import {
  useNewsletterSignup,
  useConfetti,
} from '@/helpers/newsletter.helpers';
import useFocusOnMount from '@/hooks/use-focus-on-mount';
import useToggle from '@/hooks/use-toggle';

import TextLink from '@/components/Link/TextLink';
import Spinner from '@/components/Spinner';
import Checkbox from '@/components/Checkbox';
import ColorModeContainer from '@/components/ColorModeContainer';
import ButtonRainbow from '@/components/ButtonRainbow';
import SubscribeGeyser from '@/components/SubscribeGeyser';
import SubscribeError from '@/components/SubscribeError';

// TODO: When I have an amazing newsletter issue, update this!
// const NEWSLETTER_URL = '/email/2024-07-13-deadmau5';
const NEWSLETTER_URL = null;

function DedicatedSubscribeForm() {
  const [constantConfetti, toggleConstantConfetti] = useToggle(false);

  const {
    confettiStatus,
    handleLoadConfetti,
    handleTriggerConfetti,
  } = useConfetti();

  const inputRef = React.useRef<HTMLInputElement>(null);

  useFocusOnMount(inputRef);

  const {
    status,
    emailFields,
    error,
    numOfEncounteredErrors,
    emailId,
    honeypotElem,
    handleSubmit,
  } = useNewsletterSignup({
    callbacks: {
      onStart: handleLoadConfetti,
      onSuccess: handleTriggerConfetti,
    },
  });

  if (status === 'success') {
    return (
      <>
        <SuccessWrapper>
          <SuccessMessage colorMode="light">
            <TopBorder />
            <PseudoHeading>Welcome aboard!</PseudoHeading>
            <Paragraph>
              You’re all set! In a few moments, you’ll receive an
              email from me containing the Josh Starter Pack, a
              collection of my most popular content.&nbsp;😄
            </Paragraph>
            <ConstantConfettiCheckboxWrapper htmlFor="constant-confetti">
              <Checkbox
                id="constant-confetti"
                checked={constantConfetti}
                onChange={toggleConstantConfetti}
              />
              Keep the party going!
            </ConstantConfettiCheckboxWrapper>
            <BottomBorder />
          </SuccessMessage>
        </SuccessWrapper>
        <SubscribeGeyser
          status={confettiStatus}
          mode={constantConfetti ? 'stream' : 'burst'}
        />
      </>
    );
  }

  return (
    <>
      <PrefixParagraph>Enter your email to join:</PrefixParagraph>

      <Wrapper
        as="form"
        // More problems with `as`
        // @ts-ignore
        onSubmit={handleSubmit}
      >
        {honeypotElem}

        <Label htmlFor={emailId}>Email:</Label>

        <InputOuterWrapper>
          <InputWrapper colorMode="light">
            <EmailInput id={emailId} {...emailFields} />
            <SubmitButton disabled={status === 'submitting'}>
              {status === 'submitting' ? (
                <Spinner size={20} />
              ) : (
                'Submit'
              )}
            </SubmitButton>
          </InputWrapper>
        </InputOuterWrapper>

        <AfterForm>
          <ErrorMessage
            status={status}
            error={error}
            numOfEncounteredErrors={numOfEncounteredErrors}
            fallback={
              <Disclaimer>
                If you change your mind, unsubscribing is a simple
                1-click affair, no hoops to jump through.
                {!!NEWSLETTER_URL && (
                  <>
                    {' '}
                    <TextLink target="_blank" href={NEWSLETTER_URL}>
                      Check out a recent issue
                    </TextLink>
                    .
                  </>
                )}
              </Disclaimer>
            }
          />
        </AfterForm>
      </Wrapper>
      <SubscribeGeyser status={confettiStatus} mode="burst" />
    </>
  );
}

const PrefixParagraph = styled.p`
  font-size: 1.125rem;
  margin-bottom: 2rem;
`;

const Wrapper = styled.div`
  max-width: 38rem;
  margin-top: 16px;
  margin-left: -16px;

  @media ${BREAKPOINTS.smAndSmaller} {
    margin-left: 0;
  }
`;

const SuccessWrapper = styled(Wrapper)`
  margin-top: 64px;
  animation: swoopIn 900ms both cubic-bezier(0.17, 0.67, 0.39, 0.99);
  animation-delay: 250ms;

  @keyframes swoopIn {
    from {
      transform: translateX(-64px);
      opacity: 0;
    }
  }
`;

const Label = styled.label`
  display: none;
`;

const EmailInput = styled.input`
  display: block;
  flex: 1;
  height: 48px;
  padding-left: 8px;
  border: none;
  background: transparent;
  font-size: 1rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media ${BREAKPOINTS.smAndLarger} {
    font-size: 1.125rem;
  }
`;

const InputOuterWrapper = styled.div`
  position: relative;

  html[data-color-mode='light'] &::before {
    content: '';
    position: absolute;
    inset: -10px;
    background: hsl(var(--color-background-values) / 1);
    border-radius: 12px;
  }
`;

const InputWrapper = styled(ColorModeContainer)`
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: var(--color-background);
  border-radius: 9px;
  padding: 8px;

  html[data-color-mode='light'] & {
    box-shadow:
      -06px 0.6px 1px -7px hsl(210deg 100% 20% / 0.04),
      -1.4px 1.4px 2.5px -7px hsl(210deg 100% 20% / 0.056),
      -2.6px 2.6px 4.6px -7px hsl(210deg 100% 20% / 0.07),
      -5px 4.7px 8.3px -7px hsl(210deg 100% 20% / 0.084),
      -8px 8.8px 15.5px -7px hsl(210deg 100% 20% / 0.1),
      -15px 21px 37px -7px hsl(210deg 100% 20% / 0.14);
  }
`;

const SubmitButton = styled(ButtonRainbow)`
  width: 100px;
  font-size: 1rem;

  && {
    /* Arg busted class orders again! */
    min-height: 3rem;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media ${BREAKPOINTS.smAndLarger} {
    width: 125px;
    height: 48px;
    font-size: 1.125rem;
  }
`;

const AfterForm = styled.div`
  /* Necessary to prevent a layout shift when toggling between Disclaimer and ErrorMessage */
  min-height: 3rem;
  margin-top: 24px;
  padding: 0 8px;
`;

const Disclaimer = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  text-align: center;
  opacity: 0.75;
  text-wrap: balance;
`;

const ErrorMessage = styled(SubscribeError)`
  --color-primary: var(--color-error);

  p {
    margin: 0;
    font-size: 0.9375rem;
    text-align: center;
    opacity: 1;
    text-wrap: balance;
    color: var(--color-error);
  }
`;

const SuccessMessage = styled(ColorModeContainer)`
  position: relative;
  padding: 16px;
  max-width: 38rem;
  border-radius: 8px;

  html[data-color-mode='light'] & {
    box-shadow:
      -06px 0.6px 1px -7px hsl(210deg 100% 20% / 0.04),
      -1.4px 1.4px 2.5px -7px hsl(210deg 100% 20% / 0.056),
      -2.6px 2.6px 4.6px -7px hsl(210deg 100% 20% / 0.07),
      -5px 4.7px 8.3px -7px hsl(210deg 100% 20% / 0.084),
      -8px 8.8px 15.5px -7px hsl(210deg 100% 20% / 0.1),
      -15px 21px 37px -7px hsl(210deg 100% 20% / 0.14);
  }
`;

const Paragraph = styled.p`
  font-size: 1.125rem;
  text-align: center;
`;

const PseudoHeading = styled(Paragraph)`
  font-family: var(--font-family-spicy);
  font-size: 1.5rem;
  margin-bottom: 1rem;
  margin-top: 1.5rem;
`;

const ConstantConfettiCheckboxWrapper = styled.label`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 32px;
  margin-bottom: 48px;
  margin-inline: auto;
  width: fit-content;
  padding: 16px;
  border: 1px dashed var(--color-gray-300);
  border-radius: 4px;
  animation: fadeIn 500ms 2000ms both;
  font-size: 1.125rem;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
`;

const AirMailBorder = styled.div`
  --red: hsl(0deg 85% 70%);
  --blue: hsl(210deg 85% 65%);
  --blue-darker: hsl(225deg 40% 20%);
  height: 12px;
  border-radius: 4px;
  margin: -12px;
  background: repeating-linear-gradient(
    45deg,
    var(--red) 0px 15px,
    var(--color-background) 15px 25px,
    var(--blue) 25px 40px,
    var(--color-background) 40px 50px
  );
`;

const TopBorder = styled(AirMailBorder)`
  margin-bottom: 0;
`;
const BottomBorder = styled(AirMailBorder)`
  margin-top: 0;
`;

export default DedicatedSubscribeForm;
